import React, { useState, useEffect, Fragment } from "react";
import { useAdminMode } from "../lib/AdminModeContext";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe, Crown, Shield } from "lucide-react";
import { getNavConfig, saveAccessLevel, api } from "../lib/api";
import { toast } from "sonner";

// Desktop nav toggle board: per-user drag-and-drop layout (slots + vertical columns +
// icon/full style) and owner right-click access levels (saved globally per page).
export const NAV_SLOTS = ["before", "after", "end", "page"];
// Buttons that belong to the GLOBAL nav; everything else is page-specific and defaults to the page bar.
const GLOBAL_KEYS = new Set(["surf", "favourite", "tabfav", "notify", "changelog", "glossary", "leagues", "navhide", "navextend", "borders", "arrange", "arrange_reset", "studio", "tstudio", "display", "favtoggles", "disp_theme", "disp_wide", "disp_full", "disp_page", "disp_nav", "disp_device", "disp_profiles"]);
export const isPageKey = (k) => !GLOBAL_KEYS.has(k) && !String(k).startsWith("view:") && !String(k).startsWith("own_") && !String(k).startsWith("acc_");
export const NAV_DEFAULT_LAYOUT = {
    slots: {
        before: [],
        after: [["surf", "favourite", "tabfav"]],
        end: [["view:admin", "view:business", "view:creator", "view:owner"], ["navhide", "navextend", "borders", "arrange"]],
        page: [],
    },
    pv2: true,
    style: { surf: "icon", favourite: "icon", tabfav: "icon", display: "icon", navhide: "icon", navextend: "icon", borders: "icon", arrange: "icon" },
};
const ALL_DEV = ["mobile", "tablet", "desktop"];
const DEVICE_DEFAULTS = Object.fromEntries(["disp_wide", "disp_full", "disp_page", "disp_nav", "disp_device", "disp_profiles", "navhide", "navextend", "borders"].map((k) => [k, ["desktop"]]));
const DEFAULT_PUBLIC = new Set(["allleagues", "allsort", "favfirst", "acc_theme", "range-day", "range-week", "range-month", "allsort-league", "allsort-time", "alltop", "reports", "favtoggles", "surf", "favourite", "tabfav", "notify", "spoiler", "delay", "leagues", "sortbar", "compact", "predict", "lb_trends", "lb_milestones", "lb_gamelogs", "lb_compare", "display", "disp_theme", "disp_wide", "disp_full", "disp_page", "disp_nav", "disp_device", "disp_profiles", "navhide", "navextend", "borders"]);
const ADMIN_DEFAULT = new Set(["view:admin", "view:business", "view:creator", "studio", "tstudio"]);
const LEVELS = [
    ["public", "Public", Globe, "Everyone, signed in or not"],
    ["admin", "Admin", Shield, "Admins"],
    ["owner", "Owner", Crown, "Only you"],
];
const LABELS = {
    studio: "Studio", tstudio: "Template Studio", surf: "Surf", favourite: "Favourite", tabfav: "Fav Tabs", notify: "Notifications",
    "view:admin": "Admin view", "view:business": "Business view", "view:creator": "Creator view", "view:owner": "Owner view",
    changelog: "Changelog", glossary: "Glossary", nhlsources: "Sources", builder: "Builder", extensions: "Extensions", compact: "View",
    spoiler: "Spoilers", spoiler_since: "Spoiler Window", lb_trends: "Trends", lb_milestones: "Milestones", lb_gamelogs: "Game Logs", lb_compare: "Compare", predict: "Predict", find: "Find", live: "Live", predicted: "Predicted", format: "Format", delay: "Delay", favtoggles: "Fav Toggles", reports: "Reports", sortbar: "Sort", leagues: "Leagues",
    navhide: "Hide Bars", navextend: "Inline / Dropdown", borders: "Borders", disp_theme: "Light / Dark", disp_wide: "Wide Nav", disp_full: "Full Screen", disp_page: "Page Size", disp_nav: "Nav Size", disp_device: "Device", disp_profiles: "Profiles", display: "Display", arrange: "Arrange", cut: "Cut", "hist:signings": "Signings", "hist:camp": "Camp", "hist:trades": "Trades", "hist:draft": "Draft", "hist:injuries": "Injuries", "hist:staff": "Staff Moves", "hist:jerseys": "Jerseys", "hist:captaincy": "Captaincy", "hist:champs": "Champions",
};
Object.assign(LABELS, { own_settings: "Settings", own_stats: "Switchboard", own_storage: "Storage Centre", own_leagues: "All Leagues", own_hub: "Admin Hub & Hiring", own_notifications: "Notifications", own_owner: "Owner", acc_account: "Account", acc_home: "Favourites", acc_settings: "Settings", acc_storage: "Storage Centre", acc_leagues: "All Leagues", acc_notifications: "Notifications", acc_objectives: "Objectives", acc_preferences: "Preferences", acc_finances: "Finances", acc_subscription: "Subscription", acc_reports: "Full Reports", acc_accounts: "Accounts & Users", acc_signout: "Sign Out" });
export const navLabel = (k) => LABELS[k] || String(k).replace(/^[a-z]+[:_]/, "").replace(/[_:-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const ROWS4 = { gridTemplateRows: "repeat(4, 32px)" };
const ICON_CLS = "[&_.leading-none]:!hidden [&_button]:!w-[32px] [&_button]:!px-0 [&_button]:!justify-center [&_button]:!gap-0";
const FULL_CLS = "[&_.leading-none]:!inline [&_button]:!w-auto [&_button]:!px-2 [&_button]:!gap-1.5 [&_button]:!justify-start";
const FILL_CLS = "[&>span]:!w-full [&_.leading-none]:!inline [&_button]:!w-full [&_button]:!px-2 [&_button]:!gap-1.5 [&_button]:!justify-center";
// Selected Page tiles: icon on top of the name, like the group tiles (smaller).
const BAR_KEY = "rosterly_pagebar_keys";
const readBar = () => { try { return new Set(JSON.parse(localStorage.getItem(BAR_KEY) || "[]")); } catch { return new Set(); } };

const cloneLayout = (l) => ({
    ...(l || {}),
    slots: Object.fromEntries(NAV_SLOTS.map((s) => [s, ((l?.slots || {})[s] || []).map((c) => [...c])])),
    style: { ...(l?.style || {}) },
});

// target: { slot, col (index | null = new column), pos } or { unplace: true }
export function moveKey(layout, key, target) {
    const n = cloneLayout(layout);
    let pos = target.pos;
    NAV_SLOTS.forEach((s) => n.slots[s].forEach((c, i) => {
        const j = c.indexOf(key);
        if (j >= 0) { c.splice(j, 1); if (s === target.slot && i === target.col && j < pos) pos -= 1; }
    }));
    if (target.unplace) {
        const ord = (target.looseKeys || n.order || []).filter((k) => k !== key);
        const bi = target.before ? ord.indexOf(target.before) : -1;
        if (bi >= 0) ord.splice(bi, 0, key); else ord.push(key);
        n.order = ord;
    } else {
        if (target.col === null || target.col === undefined) n.slots[target.slot].splice(pos, 0, [key]);
        else { if (!n.slots[target.slot][target.col]) n.slots[target.slot][target.col] = []; n.slots[target.slot][target.col].splice(pos, 0, key); }
    }
    NAV_SLOTS.forEach((s) => { n.slots[s] = n.slots[s].filter((c) => c.length); });
    return n;
}

// ONE global layout for every account (and signed-out visitors): the owner's Arrange
// saves it to the site nav config; nobody has a personal arrangement.
export function useNavToggleLayout(user) {
    const qc = useQueryClient();
    const { data: navCfg } = useQuery({ queryKey: ["nav-config"], queryFn: getNavConfig, staleTime: 5 * 60 * 1000 });
    const raw = navCfg?.nav_toggle_layout || NAV_DEFAULT_LAYOUT;
    // One-time move: page-specific buttons leave the nav for the page bar.
    const layout = raw.pv2 ? raw : { ...cloneLayout(raw), pv2: true, slots: Object.fromEntries(NAV_SLOTS.map((sl) => [sl, sl === "page" ? [] : ((raw.slots || {})[sl] || []).map((c) => c.filter((k) => !isPageKey(k) && !String(k).startsWith("disp_"))).filter((c) => c.length)])) };
    const save = (next) => {
        qc.setQueryData(["nav-config"], (p) => ({ ...(p || {}), nav_toggle_layout: next }));
        if (user) api.put("/admin/nav-config/toggle-layout", { nav_toggle_layout: next || null }).catch(() => toast.error("Only the owner can arrange buttons"));
    };
    return [layout, save];
}

export function useNavBoard({ buttons, layout, onLayout, pageKey, levels = {}, globalLevels = {}, publicKeys = [], isOwner, user, access = {}, arrange, force = null, hide = null, devices = {}, mobileStyle = {} }) {
    const { allButtons } = useAdminMode();
    const qc = useQueryClient();
    const [dragKey, setDragKey] = useState(null);
    const [over, setOver] = useState(null); // { key, after } drop indicator
    const [menu, setMenu] = useState(null);
    // Buttons moved onto the nav bar are part of the owner's global layout too.
    const barKeys = new Set(Array.isArray(layout?.bar) ? layout.bar : [...readBar()]);
    const toggleBar = (k) => { const n = new Set(barKeys); if (n.has(k)) n.delete(k); else n.add(k); onLayout({ ...layout, bar: [...n] }); };
    const [peek, setPeek] = useState(null);
    const hoverOn = true;
    const [look, setLook] = useState(null);
    useEffect(() => { if (arrange) setPeek(null); }, [arrange]);
    // Live colours: re-read the hovered toggle whenever its state (style/class) flips on/off.
    useEffect(() => {
        if (!peek) { setLook(null); return undefined; }
        const read = () => {
            if (!peek.el.isConnected) { setPeek(null); return; }
            const btn = peek.el.querySelector("button") || peek.el;
            const raw = btn.getBoundingClientRect();
            // Monitor presets (1080/1440/4K) zoom <html>: fixed boxes are in zoomed CSS px → convert viewport px.
            const b = document.body;
            const Z = b && b.offsetWidth ? (b.getBoundingClientRect().width / b.offsetWidth) || 1 : 1;
            const r = { top: raw.top / Z, left: raw.left / Z, right: raw.right / Z, width: raw.width / Z, height: raw.height / Z };
            const vw = window.innerWidth / Z;
            const cs = window.getComputedStyle(btn);
            setLook({ r, vw, flip: r.right + 170 > vw, color: cs.color, border: cs.borderTopColor, bg: cs.backgroundColor, svg: btn.querySelector("svg")?.outerHTML || "" });
        };
        read();
        // Toggle colours animate (transition-colors) — re-read once the transition settles too.
        let t1 = null;
        const reread = () => { read(); clearTimeout(t1); t1 = setTimeout(read, 260); };
        const mo = new MutationObserver(reread);
        mo.observe(peek.el, { attributes: true, subtree: true, childList: true, attributeFilter: ["style", "class"] });
        peek.el.addEventListener("transitionend", read);
        peek.el.addEventListener("click", reread);
        const off = () => setPeek(null);
        window.addEventListener("scroll", off, true); window.addEventListener("blur", off);
        return () => {
            clearTimeout(t1); mo.disconnect();
            peek.el.removeEventListener("transitionend", read); peek.el.removeEventListener("click", reread);
            window.removeEventListener("scroll", off, true); window.removeEventListener("blur", off);
        };
    }, [peek]);
    useEffect(() => {
        if (!menu) return undefined;
        const close = (e) => { if (e.type === "keydown" && e.key !== "Escape") return; if (e.target?.closest?.("[data-nav-toggle-menu]")) return; setMenu(null); };
        window.addEventListener("mousedown", close); window.addEventListener("keydown", close);
        return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", close); };
    }, [menu]);

    // Page buttons: level per page. Global buttons (Surf, Favourite…): ONE level for every page ("*").
    // ONE effective level per button — the same value drives desktop, mobile, the owner's public view and the menu.
    const rawLevel = (k) => { const v = isPageKey(k) ? levels[k] : globalLevels[k]; return v === "signed_in" ? "public" : v; };
    const levelOf = (k) => rawLevel(k) || (publicKeys.includes(k) || DEFAULT_PUBLIC.has(k) || String(k).startsWith("hist:") ? "public" : ADMIN_DEFAULT.has(k) ? "admin" : "owner");
    const explicit = (k) => !!((isPageKey(k) ? levels[k] : globalLevels[k]) || publicKeys.includes(k));
    const canSee = (b) => {
        // Owner sees exactly what the public sees (+ the Owner button) unless Admin Tools is on.
        if (isOwner) return !!force?.has(b.key) || allButtons || b.key === "view:owner" || levelOf(b.key) === "public";
        if (b.key === "view:owner") return false;
        const lv = levelOf(b.key);
        if (!explicit(b.key) && lv !== "public") return !!b.eligible;
        return lv === "public" || (lv === "signed_in" && !!user) || (lv === "admin" && !!access.admin)
            || (lv === "business" && !!access.business) || (lv === "creator" && !!access.creator);
    };
    // "Shows on": explicit owner choice, else the default (display settings = desktop only, Sort = phones/tablets).
    const devicesOf = (k) => devices[k] || DEVICE_DEFAULTS[k] || ALL_DEV;
    const onDev = (k, d) => devicesOf(k).includes(d);
    const visible = buttons.filter((b) => canSee(b) && (arrange || (onDev(b.key, "desktop") && !hide?.has(b.key))));
    const setDevices = (k, list) => {
        const isDefault = list.join() === (DEVICE_DEFAULTS[k] || ALL_DEV).join();
        qc.setQueryData(["settings"], (p) => { if (!p) return p; const m = { ...(p.access_devices || {}) }; if (isDefault) delete m[k]; else m[k] = list; return { ...p, access_devices: m }; });
        api.put("/admin/settings/access-devices", { key: k, devices: isDefault ? null : list }).catch(() => { });
    };
    const byKey = new Map(visible.map((b) => [b.key, b]));
    const placed = new Set(NAV_SLOTS.flatMap((s) => ((layout.slots || {})[s] || []).flat()));

    const drop = (e, target) => {
        e.preventDefault(); e.stopPropagation();
        const k = e.dataTransfer.getData("text/plain") || dragKey;
        setDragKey(null); setOver(null);
        if (k) onLayout(moveKey(layout, k, target));
    };
    const allow = (e) => { if (arrange) e.preventDefault(); };
    // Page bar: lock in the order you SEE, then move — so any button can be dropped anywhere on it.
    const dropPage = (e, order, pos) => {
        e.preventDefault(); e.stopPropagation();
        const k = e.dataTransfer.getData("text/plain") || dragKey;
        setDragKey(null); setOver(null);
        if (!k) return;
        const base = cloneLayout(layout);
        base.slots.page = [[...order]];
        onLayout(moveKey(base, k, { slot: "page", col: 0, pos }));
    };
    const setLevel = (k, lv) => {
        const level = lv === "default" ? null : lv;
        const pg0 = isPageKey(k) ? pageKey : "*";
        qc.setQueryData(["settings"], (p) => {
            if (!p) return p;
            const pg = { ...((p.access_levels || {})[pg0] || {}) };
            if (level) pg[k] = level; else delete pg[k];
            return { ...p, access_levels: { ...(p.access_levels || {}), [pg0]: pg } };
        });
        saveAccessLevel(pg0, k, level).then(() => qc.invalidateQueries({ queryKey: ["settings"] })).catch(() => { });
    };

    const tile = (b, target) => {
        const st = target.full ? "full" : target.slot === "pagegrid" ? "icon" : target.slot === "page" ? "full" : "icon";  // nav = icons (+hover name); page bar = icon + full name; Selected Page = tiles
        const horiz = !!target.pageOrder;  // page bar is one row: drop left / right, never above / below
        const isAfter = (e) => { const r = e.currentTarget.getBoundingClientRect(); return horiz ? e.clientX > r.left + r.width / 2 : e.clientY > r.top + r.height / 2; };
        const onOver = (e) => {
            if (!arrange) return;
            e.preventDefault(); e.stopPropagation();
            const after = isAfter(e);
            if (over?.key !== b.key || over?.after !== after) setOver({ key: b.key, after });
        };
        const onTileDrop = (e) => {
            const after = isAfter(e);
            if (target.pageOrder) return dropPage(e, target.pageOrder, target.pageOrder.indexOf(b.key) + (after ? 1 : 0));
            if (target.unplace) return drop(e, { unplace: true, looseKeys: target.looseKeys, before: after ? target.next : b.key });
            return drop(e, { ...target, pos: target.pos + (after ? 1 : 0) });
        };
        const iconish = st === "icon";
        const onEnter = (e) => { if (hoverOn && iconish && !arrange) setPeek({ key: b.key, el: e.currentTarget }); };
        // Clicking may open a modal (Leagues etc.) that swallows mouseleave — drop the peek if no longer hovered.
        const onClickPeek = (e) => { const el = e.currentTarget; setTimeout(() => { if (!el.isConnected || !el.matches(":hover")) setPeek((p) => (p?.el === el ? null : p)); }, 150); };
        return (
            <div key={b.key} data-testid={`nav-toggle-${b.key}`}
                className={`relative h-full [&>*:first-child]:h-full ${st === "icon" ? ICON_CLS : st === "full" ? (target.full ? FILL_CLS : FULL_CLS) : ""} ${arrange ? `cursor-grab ${b.key === "arrange" || b.key === "arrange_reset" ? "" : "[&>*:first-child]:pointer-events-none"} ring-1 ring-dashed ring-white/30 rounded-lg` : ""} ${dragKey === b.key ? "opacity-40" : ""}`}
                draggable={!!arrange}
                onDragStart={(e) => { e.dataTransfer.setData("text/plain", b.key); e.dataTransfer.effectAllowed = "move"; setDragKey(b.key); }}
                onDragEnd={() => { setDragKey(null); setOver(null); }}
                onDragOver={onOver} onDragLeave={() => setOver((o) => (o?.key === b.key ? null : o))} onDrop={onTileDrop}
                onMouseEnter={onEnter} onClickCapture={onClickPeek} onMouseLeave={() => setPeek((p) => (p?.key === b.key ? null : p))}
                onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setPeek(null); setMenu({ key: b.key, x: e.clientX, y: e.clientY }); }}>
                {b.render()}
                {arrange && over?.key === b.key && dragKey !== b.key && (
                    <span className={`absolute rounded-full bg-[#A3E635] pointer-events-none ${horiz ? `top-0 bottom-0 w-[3px] ${over.after ? "-right-[5px]" : "-left-[5px]"}` : `left-0 right-0 h-[3px] ${over.after ? "-bottom-[3px]" : "-top-[3px]"}`}`} aria-hidden="true" />
                )}
            </div>
        );
    };
    // Divider lines: user-controlled via +/− in Arrange mode. Keys: "<slot>:<gapIndex>" or "end:loose".
    const defLines = () => [`before:${((layout.slots || {}).before || []).length}`, "after:0", "end:loose"];
    const lines = new Set(layout.lines || defLines());
    const toggleLine = (k) => { const n = cloneLayout(layout); const cur = new Set(layout.lines || defLines()); if (cur.has(k)) cur.delete(k); else cur.add(k); n.lines = [...cur]; onLayout(n); };
    const lineEl = (k) => <div key={`line-${k}`} className="self-stretch w-px bg-white/20 shrink-0" aria-hidden="true" data-testid={`nav-line-${k}`} />;
    const lineBtn = (k) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); toggleLine(k); }} data-testid={`nav-line-toggle-${k}`} title={lines.has(k) ? "Remove line" : "Add line"}
            className={`absolute top-0.5 left-1/2 -translate-x-1/2 z-10 h-4 w-4 rounded-full border text-[11px] font-black leading-none flex items-center justify-center transition-colors ${lines.has(k) ? "bg-[#EF4444]/20 border-[#EF4444]/60 text-[#FCA5A5]" : "bg-[#22C55E]/20 border-[#22C55E]/60 text-[#86EFAC]"}`}>
            {lines.has(k) ? "−" : "+"}
        </button>
    );
    const gap = (slot, pos, wide) => {
        const k = `${slot}:${pos}`;
        const noLines = true;  // no separators / +− line toggles anywhere
        if (!arrange) return !noLines && lines.has(k) ? lineEl(k) : null;
        return (
            <div key={`gap-${k}`} data-testid={`nav-drop-${slot}-${pos}`} onDragOver={allow} onDrop={(e) => drop(e, { slot, col: null, pos })}
                className="group relative w-0 self-stretch shrink-0" title="Drop here for a new column">
                <span className="absolute inset-y-0 -left-1.5 w-3 rounded hover:bg-white/10" onDragOver={allow} onDrop={(e) => drop(e, { slot, col: null, pos })} />
                {!noLines && lineBtn(k)}
                {!noLines && <span className={`absolute top-5 bottom-0 left-1/2 -translate-x-1/2 border-l-2 border-dotted ${lines.has(k) ? "border-white/60" : "border-white/20"}`} aria-hidden="true" />}
            </div>
        );
    };

    // Page bar: one row of full-name buttons; drag here (Arrange) to move a button out of the nav.
    const renderPage = (opts = {}) => {
        const placedHere = ((layout.slots || {}).page || []).flat().map((k) => byKey.get(k)).filter(Boolean);
        const loose = visible.filter((b) => !placed.has(b.key) && isPageKey(b.key));
        const pubOf = (b) => levelOf(b.key) === "public";
        const all = [...placedHere, ...loose];
        const items = [...all.filter(pubOf), ...all.filter((b) => !pubOf(b))];
        const order = items.map((b) => b.key);
        // Selected Page panel: every page button as a tile. The page bar only keeps ones switched on via right-click.
        // Selected Page: icon-only (name on hover). Buttons moved to the nav bar leave the panel.
        const inPanel = items.filter((b) => !barKeys.has(b.key));
        if (opts.grid) return inPanel.length ? (
            <div className="grid grid-flow-col gap-1 shrink-0" style={{ gridTemplateRows: "repeat(2, 32px)" }} data-testid="nav-panel-page">
                {inPanel.map((b) => tile(b, { slot: "pagegrid", col: 0, pageOrder: order }))}
            </div>
        ) : null;
        const shown = items.filter((b) => barKeys.has(b.key));
        // Empty page bar while arranging: overlay drop zone only — never adds a row or changes size.
        if (!shown.length) return arrange ? <span className="absolute inset-y-1 right-2 w-24 rounded outline outline-1 outline-dashed outline-[#A3E635]/50" data-testid="nav-slot-page" onDragOver={allow} onDrop={(e) => dropPage(e, [], 0)} title="Drop here to show a button on the page bar" /> : null;
        return (
            <div className={`flex items-center gap-1.5 h-8 ${arrange ? "rounded outline outline-1 outline-dashed outline-[#A3E635]/50" : ""}`} data-testid="nav-slot-page" data-pa={arrange ? "1" : undefined}
                onDragOver={allow} onDrop={(e) => dropPage(e, order, order.length)} title={arrange ? "Drop here to show a button on the page bar" : undefined}>
                {shown.map((b, i) => (
                    <Fragment key={b.key}>
                        {i > 0 && pubOf(b) !== pubOf(shown[i - 1]) && <span className="self-stretch w-px bg-white/20 shrink-0 mx-0.5" aria-hidden="true" data-testid="nav-page-zone-divider" />}
                        <span data-pa="1" data-zone={pubOf(b) ? "public" : "strict"} className="h-full">{tile(b, { slot: "page", col: 0, pageOrder: order })}</span>
                    </Fragment>
                ))}
            </div>
        );
    };
    const renderSlot = (slot, opts = {}) => {
        const rowSty = opts.rows ? { gridTemplateRows: `repeat(${opts.rows}, 32px)` } : ROWS4;
        const raw = (layout.slots || {})[slot] || [];
        const keep = opts.only || (() => true);
        const cols = raw.map((c, ci) => ({ ci, c, items: c.map((k) => byKey.get(k)).filter((b) => b && keep(b)) })).filter((x) => x.items.length);
        const ord = layout.order || [];
        const rank = (k) => { const i = ord.indexOf(k); return i < 0 ? 1e6 : i; };
        const looseFor = (sl) => (sl === "end" ? (b) => !isPageKey(b.key) : sl === "page" ? (b) => isPageKey(b.key) : () => false);
        const loose = slot === "end" || slot === "page" ? visible.filter((b) => !placed.has(b.key) && looseFor(slot)(b) && keep(b)).map((b, i) => [b, i]).sort((a, b2) => (rank(a[0].key) - rank(b2[0].key)) || (a[1] - b2[1])).map(([b]) => b) : [];
        if (!cols.length && !loose.length) {
            if (!arrange) return null;
            // Empty slot while arranging: an overlay drop zone — never takes space, so nothing moves.
            return <span className={`absolute inset-y-0 w-3 z-10 rounded hover:bg-white/10 ${slot === "after" || slot === "end" ? "right-0" : "left-0"}`} data-testid={`nav-slot-${slot}`} onDragOver={allow} onDrop={(e) => drop(e, { slot, col: null, pos: 0 })} />;
        }
        return (
            <div className="flex items-stretch shrink-0" data-testid={`nav-slot-${slot}`}>
                {cols.map((x, xi) => (
                    <Fragment key={`${slot}-${x.ci}`}>
                        {gap(slot, x.ci)}
                        <div className={`grid grid-flow-col gap-1 shrink-0 ${xi ? "ml-1" : ""}`} style={rowSty} data-testid={`nav-col-${slot}-${x.ci}`}
                            onDragOver={allow} onDrop={(e) => drop(e, { slot, col: x.ci, pos: x.c.length })}>
                            {x.items.map((b) => tile(b, { slot, col: x.ci, pos: x.c.indexOf(b.key), full: opts.full }))}
                        </div>
                    </Fragment>
                ))}
                {gap(slot, raw.length, !cols.length)}
                {(slot === "end" || slot === "page") && (loose.length > 0 || arrange) && (
                    <>
                        
                        <div className={`grid grid-flow-col gap-1 shrink-0 ${cols.length ? "ml-1" : ""}`} style={rowSty} data-testid="nav-col-loose"
                            onDragOver={allow} onDrop={(e) => drop(e, { unplace: true, looseKeys: loose.map((x) => x.key) })} title={arrange ? "Drop here to return a toggle to the default flow" : undefined}>
                            {loose.map((b, i) => tile(b, { unplace: true, next: loose[i + 1]?.key, looseKeys: loose.map((x) => x.key), full: opts.full }))}
                        </div>
                    </>
                )}
            </div>
        );
    };

    // Hover name: grows out of the icon square; pointer-events none so ONLY the square itself keeps it open.
    const pk = peek && look ? { key: peek.key, ...look } : null;
    const peekNode = pk ? createPortal(
        <div data-testid={`nav-toggle-peek-${pk.key}`} className="fixed z-[150] pointer-events-none flex items-center gap-2 rounded-lg border px-2 whitespace-nowrap text-[9px] font-black uppercase tracking-wider bg-[#0A0B10]/95 backdrop-blur shadow-xl nav-peek-in"
            style={{ top: pk.r.top, height: pk.r.height, color: pk.color, borderColor: pk.border, transformOrigin: pk.flip ? "right center" : "left center",
                minWidth: pk.r.width, flexDirection: pk.flip ? "row-reverse" : "row",
                ...(pk.flip ? { right: pk.vw - pk.r.right } : { left: pk.r.left }) }}>
            <span className="absolute inset-0 rounded-lg" style={{ background: pk.bg }} aria-hidden="true" />
            <span className="relative flex items-center justify-center shrink-0 [&_svg]:h-3.5 [&_svg]:w-3.5" style={{ width: pk.r.width - 18 }} dangerouslySetInnerHTML={{ __html: pk.svg }} />
            <span className="relative">{navLabel(pk.key)}</span>
        </div>, document.body) : null;
    const curLevel = menu ? levelOf(menu.key) : null;
    const pill = (on) => `h-6 px-2 rounded-md border text-[9px] font-bold uppercase transition-colors ${on ? "bg-white/15 border-white/40 text-white" : "border-white/10 text-[#94A3B8] hover:text-white"}`;
    const popup = menu ? createPortal(
        <div data-nav-toggle-menu data-testid="nav-toggle-menu" className="fixed z-[200] w-[250px] p-3 rounded-xl border border-white/15 bg-[#0A0B10]/95 backdrop-blur-xl shadow-2xl"
            style={{ left: Math.min(menu.x, window.innerWidth - 260), top: Math.min(menu.y, window.innerHeight - 340) }}>
            <div className="text-[10px] font-black uppercase tracking-[0.14em] text-white mb-2">{navLabel(menu.key)}</div>
            {isOwner && menu.key !== "view:owner" && (
                <>
                    <div className="text-[9px] uppercase tracking-wider text-[#5B6472] mb-1">Who sees it</div>
                    <div className="flex gap-1 mb-3">
                        {LEVELS.map(([v, l, Icon, tip]) => (
                            <button key={v} type="button" title={tip} data-testid={`nav-toggle-level-opt-${v}`} className={`${pill(curLevel === v)} flex-1 inline-flex items-center justify-center gap-1`} onClick={() => setLevel(menu.key, v)}>
                                <Icon className="h-3 w-3" />{l}
                            </button>
                        ))}
                    </div>
                </>
            )}
            {isOwner && (
                <>
                    <div className="text-[9px] uppercase tracking-wider text-[#5B6472] mb-1">Shows on</div>
                    <div className="flex gap-1 mb-3">
                        {[["mobile", "Mobile"], ["tablet", "Tablet"], ["desktop", "Desktop"]].map(([d, l]) => {
                            const cur = devicesOf(menu.key);
                            const on = cur.includes(d);
                            return (
                                <button key={d} type="button" data-testid={`nav-toggle-device-${d}`} className={`${pill(on)} flex-1`}
                                    onClick={() => { const n = on ? cur.filter((x) => x !== d) : [...cur, d]; if (n.length) setDevices(menu.key, ["mobile", "tablet", "desktop"].filter((x) => n.includes(x))); }}>{l}</button>
                            );
                        })}
                    </div>
                </>
            )}
            <div className="flex gap-1 flex-wrap">
                {isPageKey(menu.key) && <button type="button" data-testid="nav-toggle-pagebar" className={pill(barKeys.has(menu.key))} onClick={() => toggleBar(menu.key)}>{barKeys.has(menu.key) ? "Move back to Selected Page" : "Move to nav bar (full name)"}</button>}
            </div>
        </div>, document.body) : null;

    // Mobile Access sheet: SAME buttons as desktop, sorted into Public | Admin | Owner by their access level.
    const tierOf = (b) => {
        if (b.key === "view:owner") return "owner";
        const lv = levelOf(b.key);
        return lv === "public" || lv === "signed_in" ? "public" : lv === "owner" ? "owner" : "admin";
    };
    const dev = typeof document !== "undefined" && (document.documentElement.classList.contains("is-tablet") || window.innerWidth >= 640) ? "tablet" : "mobile";
    const mobileList = buttons.filter((b) => (isOwner ? true : canSee(b)) && onDev(b.key, dev)).map((b) => ({ ...b, tier: tierOf(b) }));
    return { renderSlot, renderPage, mobileList, popup: <>{popup}{peekNode}</> };
}
